import { useCallback, useEffect, useMemo, useState } from 'react';
import { importOrgAssetToProject } from '../../lib/api';
import {
  fetchOrgAssetDetailIfStale,
  fetchOrgAssetsIfStale,
  useAssetsStore,
  useOrgAssetDetailsStore,
  useOrgAssetsData,
} from '../../stores';
import { getAssetMediaType, getAssetMediaUrl } from '../../utils/assetImageResolver';

const hasMedia = (asset) => Boolean(getAssetMediaUrl(asset));

const mediaFields = (asset) => ({
  mediaType: getAssetMediaType(asset),
  imageUrl: asset?.imageUrl || asset?.image_url || null,
  videoUrl: asset?.videoUrl || asset?.video_url || null,
  audioUrl: asset?.audioUrl || asset?.audio_url || null,
  durationMs: asset?.durationMs ?? asset?.duration_ms ?? null,
});

const toProjectMentionItem = (asset, subAssets = [], isAllowedMediaType = () => true) => ({
  id: asset.id,
  name: asset.name || 'Asset',
  label: asset.name || 'Asset',
  displayName: asset.name || 'Asset',
  ...mediaFields(asset),
  type: 'asset',
  mentionSource: 'project',
  mentionSection: 'Project Assets',
  variations: subAssets
    .filter((subAsset) => hasMedia(subAsset) && isAllowedMediaType(subAsset))
    .map((subAsset) => toProjectVariationMentionItem(subAsset, asset.id)),
});

const toProjectVariationMentionItem = (asset, parentAssetId) => ({
  id: asset.id,
  parentAssetId,
  name: asset.name || 'Variation',
  label: asset.name || 'Variation',
  displayName: asset.name || 'Variation',
  ...mediaFields(asset),
  type: 'asset',
  mentionSource: 'project',
  mentionSection: 'Project Assets',
});

const toGlobalMentionItem = (asset, matchingProjectAsset = null, subAssets = [], isAllowedMediaType = () => true) => ({
  id: `global-${asset.id}`,
  orgAssetId: asset.id,
  name: asset.name || 'Asset',
  label: asset.name || 'Asset',
  displayName: asset.name || 'Asset',
  ...mediaFields(asset),
  type: 'asset',
  mentionSource: 'global',
  mentionSection: 'Global Assets',
  matchingProjectAsset: matchingProjectAsset ? toProjectMentionItem(matchingProjectAsset, [], isAllowedMediaType) : null,
  variations: subAssets
    .filter((subAsset) => hasMedia(subAsset) && isAllowedMediaType(subAsset))
    .map((subAsset) => ({
      id: `global-${subAsset.id}`,
      orgAssetId: subAsset.id,
      parentAssetId: asset.id,
      name: subAsset.name || 'Variation',
      label: subAsset.name || 'Variation',
      displayName: subAsset.name || 'Variation',
      ...mediaFields(subAsset),
      type: 'asset',
      mentionSource: 'global',
      mentionSection: 'Global Assets',
    })),
});

/**
 * Adds project/global asset mention behavior to editor prompt item lists.
 *
 * Global assets are displayed in the same @ picker as project assets. Selecting
 * a global asset imports it into the project before PromptBox attaches it as a
 * reference image.
 */
export function useAssetMentionSupport({
  projectId,
  projectAssets = [],
  items = [],
  includeGlobalAssets = true,
  excludeAssetIds = [],
  // Optional: restrict mentions to assets whose mediaType is in this set.
  // E.g. ['image'] for image-only models, ['image','audio'] for veed-fabric.
  // When undefined, no media-type filter is applied (all types shown).
  acceptsMediaTypes,
} = {}) {
  const addAssetToStore = useAssetsStore((state) => state.addAsset);
  const { items: globalAssets = [] } = useOrgAssetsData();
  const orgAssetDetailEntries = useOrgAssetDetailsStore((state) => state.entries);
  const [importedProjectAssets, setImportedProjectAssets] = useState([]);
  const excludedAssetIds = useMemo(() => new Set(excludeAssetIds.filter(Boolean)), [excludeAssetIds]);
  const allowedMediaTypes = useMemo(
    () => (Array.isArray(acceptsMediaTypes) ? new Set(acceptsMediaTypes) : null),
    [acceptsMediaTypes],
  );
  const isAllowedMediaType = useCallback(
    (asset) => !allowedMediaTypes || allowedMediaTypes.has(getAssetMediaType(asset)),
    [allowedMediaTypes],
  );

  useEffect(() => {
    if (includeGlobalAssets) {
      fetchOrgAssetsIfStale();
    }
  }, [includeGlobalAssets]);

  const mentionableItems = useMemo(() => {
    const projectAssetByImageUrl = new Map();
    const projectMentionItems = [];
    const nestedProjectAssetIds = new Set();

    const collectNestedProjectAssetIds = (assets) => {
      (assets || []).forEach((asset) => {
        (asset?.subAssets || []).forEach((subAsset) => {
          if (subAsset?.id) {
            nestedProjectAssetIds.add(subAsset.id);
          }
          collectNestedProjectAssetIds([subAsset]);
        });
      });
    };

    collectNestedProjectAssetIds([...(Array.isArray(projectAssets) ? projectAssets : []), ...importedProjectAssets]);

    const normalizedItems = (Array.isArray(items) ? items : []).reduce((acc, item) => {
      if (item?.type === 'asset') {
        // Project assets are normalized from `projectAssets` below so that
        // nested variations do not appear as flat top-level mention results.
        return acc;
      }
      if (item?.type === 'org-asset') {
        if (!hasMedia(item) || !isAllowedMediaType(item)) return acc;
        const detail = orgAssetDetailEntries?.[item.orgAssetId || item.id]?.data;
        const subAssets = detail?.subAssets || item.variations || [];
        acc.push({
          ...item,
          ...mediaFields(item),
          variations: subAssets
            .filter((subAsset) => (
              hasMedia(subAsset) &&
              isAllowedMediaType(subAsset) &&
              !excludedAssetIds.has(subAsset.orgAssetId || subAsset.id)
            ))
            .map((subAsset) => ({
              id: subAsset.id,
              orgAssetId: subAsset.orgAssetId || subAsset.id,
              parentAssetId: item.orgAssetId || item.id,
              name: subAsset.name || subAsset.displayName || 'Variation',
              label: subAsset.label || subAsset.name || subAsset.displayName || 'Variation',
              displayName: subAsset.displayName || subAsset.name || 'Variation',
              ...mediaFields(subAsset),
              type: 'org-asset',
              mentionSection: item.mentionSection || 'Global Assets',
            })),
        });
        return acc;
      }
      acc.push(item);
      return acc;
    }, []);

    [...(Array.isArray(projectAssets) ? projectAssets : []), ...importedProjectAssets].forEach((asset) => {
      if (
        !hasMedia(asset) ||
        !isAllowedMediaType(asset) ||
        asset?.parentAssetId ||
        nestedProjectAssetIds.has(asset.id) ||
        excludedAssetIds.has(asset.id)
      ) return;
      const dedupKey = getAssetMediaUrl(asset);
      if (dedupKey && projectAssetByImageUrl.has(dedupKey)) return;
      const subAssets = (asset.subAssets || [])
        .filter((subAsset) => (
          hasMedia(subAsset) &&
          isAllowedMediaType(subAsset) &&
          !excludedAssetIds.has(subAsset.id)
        ));
      const mentionItem = toProjectMentionItem(asset, subAssets, isAllowedMediaType);
      if (dedupKey) projectAssetByImageUrl.set(dedupKey, mentionItem);
      projectMentionItems.push(mentionItem);
    });

    if (!includeGlobalAssets) return [...normalizedItems, ...projectMentionItems];

    const globalItems = (Array.isArray(globalAssets) ? globalAssets : [])
      .filter((asset) => hasMedia(asset) && isAllowedMediaType(asset))
      .map((asset) => {
        const detail = orgAssetDetailEntries?.[asset.id]?.data;
        const subAssets = (detail?.subAssets || asset.subAssets || [])
          .filter((subAsset) => (
            isAllowedMediaType(subAsset) &&
            !excludedAssetIds.has(subAsset.id)
          ));
        const dedupKey = getAssetMediaUrl(asset);
        return toGlobalMentionItem(
          asset,
          dedupKey ? projectAssetByImageUrl.get(dedupKey) || null : null,
          subAssets,
          isAllowedMediaType
        );
      })
      .filter((item) => !item.matchingProjectAsset);

    return [...normalizedItems, ...projectMentionItems, ...globalItems];
  }, [excludedAssetIds, globalAssets, importedProjectAssets, includeGlobalAssets, isAllowedMediaType, items, orgAssetDetailEntries, projectAssets]);

  const handleMentionSelect = useCallback(async (item) => {
    if (item?.mentionSource !== 'global') {
      return item;
    }

    if (item.matchingProjectAsset) {
      return item.matchingProjectAsset;
    }

    if (!projectId || !item.orgAssetId) {
      return item;
    }

    const response = await importOrgAssetToProject(projectId, item.orgAssetId);
    if (response?.asset) {
      addAssetToStore(response.asset);
      setImportedProjectAssets((current) => (
        current.some((asset) => asset.id === response.asset.id)
          ? current
          : [response.asset, ...current]
      ));
      return toProjectMentionItem(response.asset, response.asset.subAssets || [], isAllowedMediaType);
    }

    return item;
  }, [addAssetToStore, isAllowedMediaType, projectId]);

  const handleMentionHover = useCallback((item) => {
    if (!item?.orgAssetId || item.parentAssetId) return;
    fetchOrgAssetDetailIfStale({ id: item.orgAssetId }).catch((err) => {
      console.warn('[useAssetMentionSupport] Failed to load global asset variations:', err);
    });
  }, []);

  return { mentionableItems, handleMentionSelect, handleMentionHover };
}
